import React, { useState, useMemo } from 'react';
import { Calculator, Euro, Package, TrendingUp, Users, FileText, Plus, Minus, RefreshCw, Info, Zap, PieChart, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { SimulateurExpert } from './BatimentSimulateurExpert';
export { SimulateurExpert };

// ==================== SIMULATEUR DEMANDEUR ====================
export function SimulateurDemandeur() {
  const [selections, setSelections] = useState({
    expertiseSimple: 0,
    expertiseTerrain: 0,
    rapportComplet: 0,
    constat: 0,
    litigeHeures: 0
  });

  const [modePaiement, setModePaiement] = useState('acte'); // acte, credits
  const [_packChoisi, _setPackChoisi] = useState(null);

  // Tarifs à l'acte
  const tarifsActe = {
    expertiseSimple: 90,
    expertiseTerrain: 250,
    rapportComplet: 390,
    constat: 150,
    litigeHeures: 85
  };

  // Coûts en crédits
  const coutsCredits = {
    expertiseSimple: 50,
    expertiseTerrain: 120,
    rapportComplet: 180,
    constat: 70,
    litigeHeures: 40 // par 30 min
  };

  // Packs disponibles
  const packs = [
    { id: 'start', nom: 'Pack Start', prix: 50, credits: 60, reduction: 17 },
    { id: 'expert', nom: 'Pack Expert', prix: 100, credits: 130, reduction: 23 },
    { id: 'pro', nom: 'Pack Pro', prix: 250, credits: 350, reduction: 28 },
    { id: 'entreprise', nom: 'Pack Entreprise', prix: 500, credits: 750, reduction: 33 },
  ];

  // Calculs automatiques
  const calculs = useMemo(() => {
    // Total à l'acte
    const totalActe = 
      (selections.expertiseSimple * tarifsActe.expertiseSimple) +
      (selections.expertiseTerrain * tarifsActe.expertiseTerrain) +
      (selections.rapportComplet * tarifsActe.rapportComplet) +
      (selections.constat * tarifsActe.constat) +
      (selections.litigeHeures * tarifsActe.litigeHeures);

    // Total crédits nécessaires
    const totalCredits = 
      (selections.expertiseSimple * coutsCredits.expertiseSimple) +
      (selections.expertiseTerrain * coutsCredits.expertiseTerrain) +
      (selections.rapportComplet * coutsCredits.rapportComplet) +
      (selections.constat * coutsCredits.constat) +
      (selections.litigeHeures * 2 * coutsCredits.litigeHeures); // x2 car 30min

    // Trouver le pack optimal
    const packOptimal = packs.find(p => p.credits >= totalCredits) || packs[packs.length - 1];
    
    // Coût avec crédits (pack optimal)
    const coutAvecCredits = packOptimal ? packOptimal.prix : 0;
    
    // Économie potentielle
    const economie = totalActe - coutAvecCredits;
    const pourcentageEconomie = totalActe > 0 ? ((economie / totalActe) * 100).toFixed(1) : 0;

    // Crédits restants après utilisation
    const creditsRestants = packOptimal ? packOptimal.credits - totalCredits : 0;

    return {
      totalActe,
      totalCredits,
      packOptimal,
      coutAvecCredits,
      economie,
      pourcentageEconomie,
      creditsRestants
    };
  }, [selections]);

  const updateSelection = (key, delta) => {
    setSelections(prev => ({
      ...prev,
      [key]: Math.max(0, prev[key] + delta)
    }));
  };

  const resetSimulation = () => {
    setSelections({
      expertiseSimple: 0,
      expertiseTerrain: 0,
      rapportComplet: 0,
      constat: 0,
      litigeHeures: 0
    });
  };

  return (
    <Card className="border-2 border-blue-200">
      <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-2">
          <Calculator className="w-6 h-6" />
          Simulateur Demandeur - Calculez vos économies
        </CardTitle>
        <CardDescription className="text-blue-100">
          Sélectionnez vos prestations et comparez les options de paiement
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        {/* Sélection des prestations */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Sélectionnez vos prestations
            </h4>
            
            {[
              { key: 'expertiseSimple', label: 'Expertise simple', prix: 90, credits: 50 },
              { key: 'expertiseTerrain', label: 'Expertise terrain', prix: 250, credits: 120 },
              { key: 'rapportComplet', label: 'Rapport complet', prix: 390, credits: 180 },
              { key: 'constat', label: 'Constat', prix: 150, credits: 70 },
              { key: 'litigeHeures', label: 'Litige (heures)', prix: 85, credits: '40/30min' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.prix}€ / {item.credits} crédits</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => updateSelection(item.key, -1)}
                    disabled={selections[item.key] === 0}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center font-bold text-lg">{selections[item.key]}</span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => updateSelection(item.key, 1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            <Button variant="outline" className="w-full" onClick={resetSimulation}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          </div>

          {/* Résultats */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-green-600" />
              Résultats de la simulation
            </h4>

            {/* Comparaison */}
            <div className="grid grid-cols-2 gap-4">
              <Card className={`${modePaiement === 'acte' ? 'ring-2 ring-blue-500' : ''}`}>
                <CardContent className="p-4 text-center cursor-pointer" onClick={() => setModePaiement('acte')}>
                  <p className="text-sm text-gray-600 mb-1">Paiement à l'acte</p>
                  <p className="text-3xl font-bold text-gray-900">{calculs.totalActe}€</p>
                </CardContent>
              </Card>
              <Card className={`bg-green-50 ${modePaiement === 'credits' ? 'ring-2 ring-green-500' : ''}`}>
                <CardContent className="p-4 text-center cursor-pointer" onClick={() => setModePaiement('credits')}>
                  <p className="text-sm text-gray-600 mb-1">Avec crédits</p>
                  <p className="text-3xl font-bold text-green-600">{calculs.coutAvecCredits}€</p>
                  {calculs.economie > 0 && (
                    <Badge className="bg-green-700 text-white mt-1 font-semibold">
                      -{calculs.pourcentageEconomie}%
                    </Badge>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Détails */}
            {calculs.totalCredits > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Crédits nécessaires</span>
                      <span className="font-bold">{calculs.totalCredits} crédits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pack recommandé</span>
                      <span className="font-bold text-blue-600">{calculs.packOptimal?.nom}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Crédits restants</span>
                      <span className="font-bold text-green-600">+{calculs.creditsRestants}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-blue-200">
                      <span className="text-gray-700 font-medium">Économie totale</span>
                      <span className="font-bold text-green-600 text-lg">{calculs.economie}€</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommandation */}
            {calculs.economie > 0 && (
              <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5" />
                  <span className="font-semibold">Recommandation</span>
                </div>
                <p className="text-sm">
                  En choisissant le <strong>{calculs.packOptimal?.nom}</strong>, vous économisez{' '}
                  <strong>{calculs.economie}€</strong> ({calculs.pourcentageEconomie}%) 
                  et conservez <strong>{calculs.creditsRestants} crédits</strong> pour vos prochaines demandes.
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== SIMULATEUR EXPERT ====================

// ==================== TABLEAU COMPARATIF PACKS ====================
export function TableauComparatifPacks() {
  const packs = [
    { nom: 'Pack Start', prix: 50, credits: 60, valeur: 60, reduction: 17, prixCredit: 0.83 },
    { nom: 'Pack Expert', prix: 100, credits: 130, valeur: 130, reduction: 23, prixCredit: 0.77, popular: true },
    { nom: 'Pack Pro', prix: 250, credits: 350, valeur: 350, reduction: 28, prixCredit: 0.71 },
    { nom: 'Pack Entreprise', prix: 500, credits: 750, valeur: 750, reduction: 33, prixCredit: 0.67 },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-6 h-6 text-blue-600" />
          Tableau comparatif des Packs Crédits
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-4 text-left font-semibold text-gray-700">Pack</th>
                <th className="p-4 text-center font-semibold text-gray-700">Prix</th>
                <th className="p-4 text-center font-semibold text-gray-700">Crédits</th>
                <th className="p-4 text-center font-semibold text-gray-700">Valeur réelle</th>
                <th className="p-4 text-center font-semibold text-gray-700">Prix/crédit</th>
                <th className="p-4 text-center font-semibold text-gray-700">Économie</th>
              </tr>
            </thead>
            <tbody>
              {packs.map((pack, index) => (
                <tr 
                  key={index} 
                  className={`border-b ${pack.popular ? 'bg-blue-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{pack.nom}</span>
                      {pack.popular && (
                        <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-semibold rounded-full px-2.5 py-0.5 shadow-sm shadow-blue-500/20 ring-1 ring-inset ring-white/15">POPULAIRE</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-center font-bold text-lg">{pack.prix}€</td>
                  <td className="p-4 text-center font-bold text-blue-600">{pack.credits}</td>
                  <td className="p-4 text-center text-gray-600">{pack.valeur}€</td>
                  <td className="p-4 text-center text-gray-600">{pack.prixCredit}€</td>
                  <td className="p-4 text-center">
                    <Badge className="bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-full px-3 py-1 shadow-sm shadow-emerald-500/20 ring-1 ring-inset ring-white/15">-{pack.reduction}%</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== GRILLE TARIFAIRE COMPLÈTE ====================
export function GrilleTarifaire() {
  const services = [
    { 
      categorie: 'Expertises',
      items: [
        { nom: 'Expertise simple (lecture + avis)', tarifActe: 90, credits: 50, duree: '1-2 jours' },
        { nom: 'Expertise complète terrain', tarifActe: 250, credits: 120, duree: '3-5 jours' },
        { nom: 'Rapport contradictoire', tarifActe: 390, credits: 180, duree: '5-10 jours' },
      ]
    },
    {
      categorie: 'Constats & Litiges',
      items: [
        { nom: 'Constat pour assurance / civil', tarifActe: 150, credits: 70, duree: '2-3 jours' },
        { nom: 'Accompagnement litige (1h)', tarifActe: 85, credits: 40, duree: 'Sur RDV' },
      ]
    }
  ];

  return (
    <Card>
      <CardHeader className="bg-gradient-to-r from-slate-600 to-gray-700 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-2">
          <Euro className="w-6 h-6" />
          Grille Tarifaire Complète - FOGEDOM Bâtiment & Expertise
        </CardTitle>
        <CardDescription className="text-gray-300">
          Tarifs Global • Territoire France Outre-mer • 2025
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {services.map((section, sIdx) => (
          <div key={sIdx}>
            <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">
              {section.categorie}
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="p-3 text-left">Prestation</th>
                  <th className="p-3 text-center">Tarif à l'acte</th>
                  <th className="p-3 text-center">Coût en crédits</th>
                  <th className="p-3 text-center">Délai moyen</th>
                </tr>
              </thead>
              <tbody>
                {section.items.map((item, iIdx) => (
                  <tr key={iIdx} className={iIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-3 text-gray-800">{item.nom}</td>
                    <td className="p-3 text-center font-bold text-blue-600">{item.tarifActe}€</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-semibold px-3 py-1.5 shadow-sm shadow-blue-500/20 ring-1 ring-inset ring-white/15">
                        <Zap className="w-3 h-3 opacity-90" />
                        {item.credits}
                        <span className="opacity-80 font-medium">crédits</span>
                      </span>
                    </td>
                    <td className="p-3 text-center text-gray-500 text-sm">{item.duree}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        
        <div className="p-4 bg-blue-50 border-t border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p><strong>Note :</strong> Les tarifs sont indicatifs et peuvent varier selon la complexité du dossier.</p>
              <p className="mt-1">Achetez des packs crédits pour économiser jusqu'à 33%.</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default { SimulateurDemandeur, SimulateurExpert, TableauComparatifPacks, GrilleTarifaire };
